@if (loading()) {
    <div class="text-center">
        <div class="spinner-border" role="status">
            <span class="visually-hidden">Loading...</span>
        </div>
    </div>
} @else if (activeSMPI(); as smpi) {
    <!-- Year selector (only shown when multiple years exist) -->
    @if (smpiYears().length > 1) {
        <div class="smpi-year-selector">
            @for (year of smpiYears(); track year) {
                <button class="smpi-year-tile" [class.active]="activeYear() === year" (click)="setActiveYear(year)">
                    {{ year }}
                </button>
            }
        </div>
    }
    <!-- Table Type 1: Single Account -->
    @if (smpi.SMPI_TABLE === 1) {
        <table class="table smpi-table">
            <thead>
                <tr>
                    <th rowspan="2">{{ fundColumnHeading }}</th>
                    <th rowspan="2">{{ investmentTypeColumnHeading }}</th>
                    <th colspan="2">{{ getAccountGroupHeadingForType(1) }}</th>
                </tr>
                <tr>
                    <th class="text-right">{{ unitsColumnHeading }}</th>
                    <th class="text-right">{{ valueColumnHeading }}</th>
                </tr>
            </thead>
            @for (n of fundRange(); track n) {
                <tbody>
                    @if (isGlidepath(n)) {
                        <tr>
                            <th colspan="4" class="smpi-cell-grey glidepath">
                                <strong>{{ smpi.GLIDEPATH_SMPI || glidepathFallbackLabel }}</strong>
                            </th>
                        </tr>
                    }
                    <tr>
                        <td [class.smpi-cell-grey]="isGreyCell(n)">{{ smpi['FUND_' + n] }}</td>
                        <td [class.smpi-cell-grey]="isGreyCell(n)">{{ smpi['ASSET_' + n] }}</td>
                        <td class="text-right" [class.smpi-cell-grey]="isGreyCell(n)">{{ smpi['UNITS_' + n] }}</td>
                        <td class="text-right" [class.smpi-cell-grey]="isGreyCell(n)">
                            {{ currencySymbol }}{{ formatMoney(smpi['VALUE_' + n]) }}
                        </td>
                    </tr>
                </tbody>
            }
            <tbody>
                <tr>
                    <td colspan="3">
                        <strong>{{ totalLabel }}</strong>
                    </td>
                    <td class="text-right">
                        <strong>{{ currencySymbol }}{{ formatMoney(smpi.TOTAL_CFV) }}</strong>
                    </td>
                </tr>
            </tbody>
        </table>
    }

    <!-- Table Type 2: Dual Account (DC + Investment) -->
    @if (smpi.SMPI_TABLE === 2) {
        <table class="table smpi-table">
            <thead>
                <tr>
                    <th rowspan="2">{{ fundColumnHeading }}</th>
                    <th rowspan="2">{{ investmentTypeColumnHeading }}</th>
                    <th colspan="2">{{ accountGroupHeadingDC }}</th>
                    <th colspan="2">{{ accountGroupHeadingIA }}</th>
                </tr>
                <tr>
                    <th class="text-right">{{ unitsColumnHeading }}</th>
                    <th class="text-right">{{ valueColumnHeading }}</th>
                    <th class="text-right">{{ unitsColumnHeading }}</th>
                    <th class="text-right">{{ valueColumnHeading }}</th>
                </tr>
            </thead>
            @for (n of fundRange(); track n) {
                <tbody>
                    @if (isGlidepath(n)) {
                        <tr>
                            <th colspan="6" class="smpi-cell-grey glidepath">
                                <strong>{{ smpi.GLIDEPATH_SMPI || glidepathFallbackLabel }}</strong>
                            </th>
                        </tr>
                    }
                    <tr>
                        <td [class.smpi-cell-grey]="isGreyCell(n)">{{ smpi['FUND_' + n] }}</td>
                        <td [class.smpi-cell-grey]="isGreyCell(n)">{{ smpi['ASSET_' + n] }}</td>
                        <td class="text-right" [class.smpi-cell-grey]="isGreyCell(n)">{{ smpi['UNITS_' + n] }}</td>
                        <td class="text-right" [class.smpi-cell-grey]="isGreyCell(n)">
                            {{ currencySymbol }}{{ formatMoney(smpi['VALUE_' + n]) }}
                        </td>
                        <td class="text-right" [class.smpi-cell-grey]="isGreyCell(n)">
                            {{ smpi['UNITS_' + n + '_IA'] }}
                        </td>
                        <td class="text-right" [class.smpi-cell-grey]="isGreyCell(n)">
                            {{ currencySymbol }}{{ formatMoney(smpi['VALUE_' + n + '_IA']) }}
                        </td>
                    </tr>
                </tbody>
            }
            <tbody>
                <tr>
                    <td colspan="5">
                        <strong>{{ totalLabel }}</strong>
                    </td>
                    <td class="text-right">
                        <strong>{{ currencySymbol }}{{ formatMoney(smpi.TOTAL_CFV) }}</strong>
                    </td>
                </tr>
            </tbody>
        </table>
    }

    <!-- Table Type 3: Investment Account Only -->
    @if (smpi.SMPI_TABLE === 3) {
        <table class="table smpi-table">
            <thead>
                <tr>
                    <th rowspan="2">{{ fundColumnHeading }}</th>
                    <th rowspan="2">{{ investmentTypeColumnHeading }}</th>
                    <th colspan="2">{{ getAccountGroupHeadingForType(3) }}</th>
                </tr>
                <tr>
                    <th class="text-right">{{ unitsColumnHeading }}</th>
                    <th class="text-right">{{ valueColumnHeading }}</th>
                </tr>
            </thead>
            @for (n of fundRange(); track n) {
                <tbody>
                    @if (isGlidepath(n)) {
                        <tr>
                            <th colspan="4" class="smpi-cell-grey glidepath">
                                <strong>{{ smpi.GLIDEPATH_SMPI || glidepathFallbackLabel }}</strong>
                            </th>
                        </tr>
                    }
                    <tr>
                        <td [class.smpi-cell-grey]="isGreyCell(n)">{{ smpi['FUND_' + n] }}</td>
                        <td [class.smpi-cell-grey]="isGreyCell(n)">{{ smpi['ASSET_' + n] }}</td>
                        <td class="text-right" [class.smpi-cell-grey]="isGreyCell(n)">
                            {{ smpi['UNITS_' + n + '_IA'] }}
                        </td>
                        <td class="text-right" [class.smpi-cell-grey]="isGreyCell(n)">
                            {{ currencySymbol }}{{ formatMoney(smpi['VALUE_' + n + '_IA']) }}
                        </td>
                    </tr>
                </tbody>
            }
            <tbody>
                <tr>
                    <td colspan="3">
                        <strong>{{ totalLabel }}</strong>
                    </td>
                    <td class="text-right">
                        <strong>{{ currencySymbol }}{{ formatMoney(smpi.TOTAL_CFV) }}</strong>
                    </td>
                </tr>
            </tbody>
        </table>
    }

    <!-- No table data message -->
    @if (!smpi.SMPI_TABLE || (smpi.SMPI_TABLE !== 1 && smpi.SMPI_TABLE !== 2 && smpi.SMPI_TABLE !== 3)) {
        <p class="text-center">{{ noDataMessage }}</p>
    }

    @if (disclaimerText) {
        <div [innerHTML]="disclaimerText"></div>
    }

    @if (projectedPensionHeading) {
        <h3 [innerText]="projectedPensionHeading"></h3>
    }

    @if (getIntroTextWithTRA(); as introHtml) {
        <div [innerHTML]="introHtml"></div>
    }

    <table class="table smpi-table smpi-projected-pension">
        <thead>
            <tr>
                <td>&nbsp;</td>
                <th colspan="2" class="text-center">
                    {{ projectedColumnHeading }} <sup>(1) (2)</sup> <br /><small>(in current money terms)</small>
                </th>
            </tr>
            <tr>
                <td>&nbsp;</td>
                <th>{{ scenarioALabel }}</th>
                <th>{{ scenarioBLabel }}</th>
            </tr>
        </thead>
        <tbody>
            <tr>
                <th>
                    <strong>{{ projectedFundLabel }}</strong>
                </th>
                <td>{{ currencySymbol }}{{ formatMoney(smpi.SCENARIO_A_FUND) }}</td>
                <td>{{ currencySymbol }}{{ formatMoney(smpi.SCENARIO_B_FUND) }}</td>
            </tr>
            <tr>
                <th>
                    <strong>{{ estimatedPensionLabel }}</strong>
                </th>
                <td>{{ currencySymbol }}{{ formatMoney(smpi.SCENARIO_A_PENSION) }}</td>
                <td>{{ currencySymbol }}{{ formatMoney(smpi.SCENARIO_B_PENSION) }}</td>
            </tr>
        </tbody>
    </table>

    @if (footnoteText) {
        <div [innerHTML]="footnoteText"></div>
    }

    @if (assumptionsText) {
        <div [innerHTML]="assumptionsText"></div>
    }
} @else {
    <p class="text-center">{{ noDataMessage }}</p>
}
